import './polyfills/Object.assign';
import './polyfills/array.find';
import './polyfills/node.contains';
import './polyfills/performance.now';
import './polyfills/Promise';
import './polyfills/requestAnimationFrame';

import defaults from './blcktive/config/defaults';
import easing from './blcktive/static/easing';
import interpolators from './blcktive/static/interpolators';
import { svg, win } from './config/environment';
import proto from './blcktive/prototype';
import { extend, extendWith } from './extend/_extend';
import macro from './extend/_macro';
import parse from './parse/_parse';
import getContext from './blcktive/static/getContext';
import isInstance from './blcktive/static/isInstance';
import use from './blcktive/static/use';
import construct from './blcktive/construct';
import initialise from './blcktive/initialise';
import { getCSS, splitTag } from './global/css';
import { escapeKey, unescapeKey, normalise } from './shared/keypaths';
import { joinKeys, splitKeypath } from './blcktive/static/keypaths';
import shared from './blcktive/shared';
import { findPlugin } from './blcktive/static/findPlugin';
import parseJSON from './utils/parseJSON';
import CSSModel from './model/specials/CSSModel';
import { data as sharedData } from './model/specials/SharedModel';
import { extern } from './shared/getblcktiveContext';
import { warn } from 'utils/log';

import { batch } from './global/runloop';

import { assign, defineProperty, defineProperties } from 'utils/object';

export default function blcktive(options) {
  if (!(this instanceof blcktive)) return new blcktive(options);
  if (options && options.component) return;

  construct(this, options || {});
  initialise(this, options || {}, {});
}

// check to see if we're being asked to force blcktive as a global for some weird environments
if (win && !win.blcktive) {
  let opts = '';
  const script =
    document.currentScript ||
    /* istanbul ignore next */ document.querySelector('script[data-blcktive-options]');

  if (script) opts = script.getAttribute('data-blcktive-options') || '';

  /* istanbul ignore next */
  if (~opts.indexOf('ForceGlobal')) win.blcktive = blcktive;
} else if (win) {
  warn(`blcktive already appears to be loaded while loading BUILD_PLACEHOLDER_VERSION.`);
}

assign(blcktive.prototype, proto, defaults);
blcktive.prototype.constructor = blcktive;

// alias prototype as `defaults`
blcktive.defaults = blcktive.prototype;

// share defaults with the parser
shared.defaults = blcktive.defaults;
shared.blcktive = blcktive;

// static properties
defineProperties(blcktive, {
  // debug flag
  DEBUG: { writable: true, value: true },
  DEBUG_PROMISES: { writable: true, value: true },

  // static methods:
  extend: { value: extend },
  extendWith: { value: extendWith },
  escapeKey: { value: escapeKey },
  evalObjectString: { value: parseJSON },
  findPlugin: { value: findPlugin },
  getContext: { value: getContext },
  getCSS: { value: getCSS },
  isInstance: { value: isInstance },
  joinKeys: { value: joinKeys },
  macro: { value: macro },
  normaliseKeypath: { value: normalise },
  parse: { value: parse },
  splitKeypath: { value: splitKeypath },
  // sharedSet and styleSet are in _extend because circular refs
  unescapeKey: { value: unescapeKey },
  use: { value: use },

  // support
  enhance: { writable: true, value: false },
  svg: { value: svg },
  tick: {
    get() {
      return batch && batch.promise;
    }
  },

  // version
  VERSION: { value: 'BUILD_PLACEHOLDER_VERSION' },

  // plugins
  adaptors: { writable: true, value: {} },
  components: { writable: true, value: {} },
  decorators: { writable: true, value: {} },
  easing: { writable: true, value: easing },
  events: { writable: true, value: {} },
  extensions: { value: [] },
  helpers: { writable: true, value: defaults.helpers },
  interpolators: { writable: true, value: interpolators },
  partials: { writable: true, value: {} },
  transitions: { writable: true, value: {} },

  // CSS variables
  cssData: { configurable: true, value: {} },
  perComponentStyleElements: { get: splitTag, set: splitTag },

  // access to @shared without an instance
  sharedData: { value: sharedData },

  // for getting the source blcktive lib from a constructor
  blcktive: { value: blcktive },

  // to allow extending contexts
  Context: { value: extern.Context.prototype }
});

// cssData must already be in place
defineProperty(blcktive, '_cssModel', {
  configurable: true,
  value: new CSSModel(blcktive)
});

defineProperty(blcktive.prototype, 'rendered', {
  get() {
    return this.fragment && this.fragment.rendered;
  }
});
